/**
 * My Inbox — unified aggregate view of every task routed to any
 * department the caller has clearance for.
 *
 * Multi-department officers (Executive, HR head who also covers Legal,
 * IT + Safety, …) get one prioritized list with a filter chip per
 * department + priority. Replaces the 12-tab click-through with a
 * single unified queue.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Inbox, Filter, RefreshCw, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { DEPT_LIST } from './DeptTaskShared';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_TONE = {
  urgent: 'bg-red-600 text-white',
  high: 'bg-amber-500 text-black',
  normal: 'bg-zinc-600 text-white',
  low: 'bg-zinc-800 text-zinc-300',
};

const STATUS_TONE = {
  open: 'bg-sky-600 text-white',
  in_progress: 'bg-blue-600 text-white',
  resolved: 'bg-emerald-600 text-white',
  cancelled: 'bg-red-700 text-white',
};

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

export default function MyInboxAggregate({ accessibleDepts = [] }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState('all');
  const [filterPrio, setFilterPrio] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');

  const scopedDepts = accessibleDepts?.length ? accessibleDepts : DEPT_LIST.map((d) => d.key);

  const load = async () => {
    setLoading(true);
    try {
      // One request per accessible dept — total is small (12 max) so we
      // just fan out in parallel and merge client-side.
      const results = await Promise.all(
        scopedDepts.map((k) =>
          fetch(`${API}/api/dept-tasks?target_department=${k}`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => []),
        ),
      );
      const merged = results.flat().sort((a, b) => {
        const p = (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
        if (p !== 0) return p;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setTasks(merged);
    } catch (_) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => tasks.filter((t) => {
    if (filterDept !== 'all' && t.target_department !== filterDept) return false;
    if (filterPrio !== 'all' && t.priority !== filterPrio) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    return true;
  }), [tasks, filterDept, filterPrio, filterStatus]);

  const setStatus = async (task_id, status) => {
    try {
      const res = await fetch(`${API}/api/dept-tasks/${task_id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) { toast.success(`Marked ${status.replace('_', ' ')}`); load(); }
      else { toast.error('Update failed'); }
    } catch (_) { toast.error('Network error'); }
  };

  const counts = useMemo(() => {
    const c = { total: tasks.length, open: 0, urgent: 0 };
    tasks.forEach((t) => {
      if (t.status !== 'resolved' && t.status !== 'cancelled') c.open += 1;
      if (t.priority === 'urgent' && t.status !== 'resolved' && t.status !== 'cancelled') c.urgent += 1;
    });
    return c;
  }, [tasks]);

  return (
    <div className="space-y-4" data-testid="my-inbox-aggregate">
      <Card className="bg-zinc-900 border-2 border-[#C9A962]/40 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="p-2 rounded-lg bg-[#C9A962]/20 text-[#C9A962]">
              <Inbox className="h-6 w-6" />
            </span>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                MY INBOX — ALL DEPARTMENTS
              </h2>
              <p className="text-xs text-zinc-400">
                Unified queue across every department you have access to. Sorted by priority then recency.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-[#C9A962] text-black">{counts.open} open</Badge>
            {counts.urgent > 0 && (
              <Badge className="bg-red-600 text-white">{counts.urgent} urgent</Badge>
            )}
            <Button size="sm" variant="outline" onClick={load}
                    className="border-zinc-700 text-zinc-200 text-xs h-8"
                    data-testid="my-inbox-refresh">
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-zinc-500" />
          <FilterChip label="All depts" active={filterDept === 'all'} onClick={() => setFilterDept('all')} />
          {scopedDepts.map((k) => {
            const d = DEPT_LIST.find((x) => x.key === k);
            if (!d) return null;
            const n = tasks.filter((t) => t.target_department === k && (filterStatus === 'all' || t.status === filterStatus)).length;
            return n === 0 ? null : (
              <FilterChip
                key={k}
                label={`${d.label.split(' ')[0]} · ${n}`}
                active={filterDept === k}
                onClick={() => setFilterDept(k)}
                testid={`my-inbox-filter-dept-${k}`}
              />
            );
          })}
          <span className="h-4 w-px bg-zinc-700 mx-2" />
          <FilterChip label="All priorities" active={filterPrio === 'all'} onClick={() => setFilterPrio('all')} />
          <FilterChip label="Urgent" active={filterPrio === 'urgent'} onClick={() => setFilterPrio('urgent')} />
          <FilterChip label="High" active={filterPrio === 'high'} onClick={() => setFilterPrio('high')} />
          <span className="h-4 w-px bg-zinc-700 mx-2" />
          <FilterChip label="Open" active={filterStatus === 'open'} onClick={() => setFilterStatus('open')} />
          <FilterChip label="In progress" active={filterStatus === 'in_progress'} onClick={() => setFilterStatus('in_progress')} />
          <FilterChip label="Resolved" active={filterStatus === 'resolved'} onClick={() => setFilterStatus('resolved')} />
          <FilterChip label="All statuses" active={filterStatus === 'all'} onClick={() => setFilterStatus('all')} />
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="my-inbox-list">
        {loading ? (
          <p className="text-xs text-zinc-500 py-6 text-center">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-xs text-zinc-500 py-6 text-center">
            No tasks match your filters. Try widening the department, priority, or status filter above.
          </p>
        ) : (
          <ul className="space-y-2">
            {visible.map((t) => {
              const d = DEPT_LIST.find((x) => x.key === t.target_department);
              return (
                <li
                  key={t.task_id}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg p-4"
                  data-testid={`my-inbox-row-${t.task_id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge className="bg-[#C9A962] text-black text-[10px]">{d?.label || t.target_department}</Badge>
                        <Badge className={PRIORITY_TONE[t.priority] || PRIORITY_TONE.normal}>{t.priority.toUpperCase()}</Badge>
                        <Badge className={STATUS_TONE[t.status] || STATUS_TONE.open}>
                          {(t.status || 'open').replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm font-bold text-white">{t.subject}</p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        From {t.created_by_name || 'NECS admin'} · {new Date(t.created_at).toLocaleString()}
                        {t.related_entity && ` · Ref: ${t.related_entity}`}
                      </p>
                      {t.detail && <p className="text-xs text-zinc-300 mt-1 whitespace-pre-wrap">{t.detail}</p>}
                    </div>
                  </div>
                  {(t.status === 'open' || t.status === 'in_progress') && (
                    <div className="flex gap-2 mt-2">
                      {t.status === 'open' && (
                        <Button size="sm" variant="outline"
                                onClick={() => setStatus(t.task_id, 'in_progress')}
                                className="border-blue-500 text-blue-400 hover:bg-blue-500/10 text-xs h-7"
                                data-testid={`my-inbox-progress-${t.task_id}`}>
                          <Clock className="h-3 w-3 mr-1" /> Working on it
                        </Button>
                      )}
                      <Button size="sm"
                              onClick={() => setStatus(t.task_id, 'resolved')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7"
                              data-testid={`my-inbox-resolve-${t.task_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full border transition ${
        active ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-[#C9A962]/50'
      }`}
    >
      {label}
    </button>
  );
}
